/**
 * CampaignActivityDrilldown — expanded panel under a Campaign Schedules row.
 *
 * Answers "what has this campaign actually sent?" for someone coming back
 * after days away:
 *   • Headline — when the last send happened, how many went out, which
 *     touch, from which mailbox.
 *   • Totals — prospects reached, messages sent, replies, bounces, queued.
 *   • Send history — every send run (newest first); click one to see it.
 *   • Messages — recipient, touch, subject, sent time, sender, outcome, and
 *     the full email body on click — for one run or for all sends.
 *   • Touch progress — where the 7-touch cadence stands.
 *   • Recent skips — why queued messages have not gone out (last 7 days).
 *
 * Backend: GET /scheduler/campaign-schedules/{id}/activity and /messages.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  History,
  Inbox,
  Mail,
  RefreshCw,
  Search,
  Send,
} from "lucide-react";

import { schedulerApi } from "@/services/apiClient";
import type {
  CampaignActivity,
  CampaignMessage,
  CampaignMessageList,
  CampaignMessageStatusFilter,
  CampaignSendRun,
  CampaignTouchProgress,
} from "@/types/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn, timeAgo, toProperCase } from "@/lib/utils";

const MESSAGES_PAGE_SIZE = 25;

// ── Labels ───────────────────────────────────────────────────────────────────

const MESSAGE_STATUS_LABEL: Record<string, string> = {
  Sent: "Sent",
  Replied: "Replied",
  Bounced: "Bounced",
  MessageBlocked: "Blocked",
  Failed: "Failed",
  BatchPending: "Awaiting Confirmation",
};

const MESSAGE_STATUS_COLOR: Record<string, string> = {
  Sent: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  Replied: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
  Bounced: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  MessageBlocked: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
  Failed: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  BatchPending: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
};

const ANGLE_LABEL: Record<string, string> = {
  FirstTouch: "First Touch",
  NewEvidence: "New Evidence",
  DifferentPain: "Different Pain",
  IndustryInsight: "Industry Insight",
  DirectQuestion: "Direct Question",
  Breakup: "Breakup",
};

const STATUS_FILTER_OPTIONS: { value: "" | CampaignMessageStatusFilter; label: string }[] = [
  { value: "", label: "All Outcomes" },
  { value: "sent", label: "Sent (No Reply Yet)" },
  { value: "replied", label: "Replied" },
  { value: "opened", label: "Opened" },
  { value: "clicked", label: "Clicked" },
  { value: "bounced", label: "Bounced" },
  { value: "blocked", label: "Blocked" },
  { value: "failed", label: "Failed" },
  { value: "awaiting", label: "Awaiting Confirmation" },
];

// ── Formatting helpers ───────────────────────────────────────────────────────

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatShortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** "2:30 PM" for a single instant, "2:30 PM – 2:41 PM" for a span. */
function formatRunWindow(run: CampaignSendRun): string {
  const start = formatClock(run.startedAt);
  const end = formatClock(run.endedAt);
  if (start === end) return start;
  const sameDay = formatDay(run.startedAt) === formatDay(run.endedAt);
  return sameDay ? `${start} – ${end}` : `${start} – ${formatWhen(run.endedAt)}`;
}

function formatTouches(touches: number[]): string {
  if (touches.length === 0) return "—";
  return touches.length === 1
    ? `Touch ${touches[0]}`
    : `Touches ${touches.join(", ")}`;
}

function plural(n: number, word: string, pluralWord?: string): string {
  return `${n.toLocaleString()} ${n === 1 ? word : pluralWord ?? `${word}s`}`;
}

// ── Small pieces ─────────────────────────────────────────────────────────────

function MessageStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
        MESSAGE_STATUS_COLOR[status] ?? "bg-muted text-muted-foreground",
      )}
    >
      {MESSAGE_STATUS_LABEL[status] ?? toProperCase(status)}
    </span>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: string;
}) {
  return (
    <div className="rounded-md border bg-background px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn("text-lg font-semibold tabular-nums", tone)}>{value.toLocaleString()}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function RunOutcome({ run }: { run: CampaignSendRun }) {
  const parts: { label: string; n: number; tone: string }[] = [
    { label: "replied", n: run.replied, tone: "text-purple-600 dark:text-purple-400" },
    { label: "opened", n: run.opened, tone: "text-sky-600 dark:text-sky-400" },
    { label: "bounced", n: run.bounced, tone: "text-rose-600 dark:text-rose-400" },
    { label: "blocked", n: run.blocked, tone: "text-orange-600 dark:text-orange-400" },
    { label: "failed", n: run.failed, tone: "text-muted-foreground" },
    { label: "awaiting", n: run.awaitingConfirmation, tone: "text-blue-600 dark:text-blue-400" },
  ].filter((p) => p.n > 0);
  if (parts.length === 0) return <span className="text-muted-foreground">No replies or bounces yet</span>;
  return (
    <span className="flex flex-wrap gap-x-2">
      {parts.map((p) => (
        <span key={p.label} className={p.tone}>
          {p.n} {p.label}
        </span>
      ))}
    </span>
  );
}

/** Renders the sent email. HTML bodies go into a sandboxed iframe so the
 *  stored markup can never run script inside the app. */
function EmailBody({ body }: { body: string | null }) {
  if (!body) {
    return <p className="text-sm italic text-muted-foreground">No body stored for this message.</p>;
  }
  const isHtml = /<[a-z][\s\S]*>/i.test(body);
  if (isHtml) {
    return (
      <iframe
        title="Sent email body"
        sandbox=""
        srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:system-ui,-apple-system,Segoe UI,sans-serif;font-size:13px;line-height:1.55;color:#111;background:#fff;margin:12px;}a{color:#2563eb;}</style></head><body>${body}</body></html>`}
        className="h-72 w-full rounded-md border bg-white"
      />
    );
  }
  return (
    <p className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md border bg-background p-3 text-sm leading-relaxed">
      {body}
    </p>
  );
}

// ── Sections ─────────────────────────────────────────────────────────────────

function Headline({ data }: { data: CampaignActivity }) {
  const run = data.lastRun;
  if (!run) {
    return (
      <div className="flex items-start gap-3 rounded-md border bg-background p-3">
        <Inbox className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="text-sm">
          <div className="font-medium">This campaign has not sent anything yet.</div>
          <div className="text-muted-foreground">
            {data.scheduled > 0
              ? `${plural(data.scheduled, "message")} queued to send.`
              : "Nothing is queued to send."}
            {data.notReady > 0 && ` ${plural(data.notReady, "message")} still in draft / QA.`}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900 dark:bg-emerald-950/30">
      <Send className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
      <div className="space-y-0.5 text-sm">
        <div>
          <span className="text-muted-foreground">Last send: </span>
          <span className="font-semibold">{formatWhen(run.startedAt)}</span>
          <span className="text-muted-foreground"> ({timeAgo(run.endedAt)})</span>
        </div>
        <div>
          <span className="font-semibold">{plural(run.messageCount, "message")}</span>
          {" to "}
          <span className="font-semibold">{plural(run.recipientCount, "recipient")}</span>
          {" · "}
          {formatTouches(run.touchNumbers)}
          {run.senders.length > 0 && (
            <>
              {" · from "}
              <span className="font-medium">{run.senders.join(", ")}</span>
            </>
          )}
        </div>
        <div className="text-xs">
          <RunOutcome run={run} />
        </div>
      </div>
    </div>
  );
}

function SkipNotice({
  data,
  skipReasonLabels,
}: {
  data: CampaignActivity;
  skipReasonLabels: Record<string, string>;
}) {
  const entries = Object.entries(data.skippedLast7Days).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return (
    <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/60 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div>
        <div className="font-medium">Queued messages held back in the last 7 days</div>
        <div className="text-muted-foreground">
          {entries
            .map(([reason, n]) => `${skipReasonLabels[reason] ?? toProperCase(reason)} × ${n}`)
            .join(" · ")}
          {data.lastSkippedAt && ` — most recently ${timeAgo(data.lastSkippedAt)}`}
        </div>
      </div>
    </div>
  );
}

function SendHistory({
  data,
  selectedKey,
  onSelect,
}: {
  data: CampaignActivity;
  selectedKey: string | null;
  onSelect: (key: string) => void;
}) {
  // Group runs under their (local) calendar day so a week of sends reads
  // like a diary: "Sat, Oct 24, 2026 — 2 sends".
  const groups = useMemo(() => {
    const out: { day: string; runs: CampaignSendRun[] }[] = [];
    for (const run of data.runs) {
      const day = formatDay(run.startedAt);
      const last = out[out.length - 1];
      if (last && last.day === day) last.runs.push(run);
      else out.push({ day, runs: [run] });
    }
    return out;
  }, [data.runs]);

  return (
    <div className="rounded-md border bg-background">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <History className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Send History</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {plural(data.runsTotal, "send")}
        </span>
      </div>
      {data.runs.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground">No sends yet.</div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto">
          {groups.map((g) => (
            <div key={g.day}>
              <div className="sticky top-0 z-10 bg-muted/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur">
                {g.day}
              </div>
              {g.runs.map((run) => {
                const isLatest = data.lastRun?.runKey === run.runKey;
                const selected = selectedKey === run.runKey;
                return (
                  <button
                    key={run.runKey}
                    type="button"
                    onClick={() => onSelect(run.runKey)}
                    className={cn(
                      "block w-full border-b px-3 py-2 text-left text-xs transition-colors last:border-b-0 hover:bg-muted/60",
                      selected && "bg-primary/10 hover:bg-primary/10",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium">{formatRunWindow(run)}</span>
                      {isLatest && (
                        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                          Latest
                        </span>
                      )}
                      <span className="ml-auto font-semibold tabular-nums">
                        {plural(run.messageCount, "msg", "msgs")}
                      </span>
                    </div>
                    <div className="mt-0.5 pl-5 text-muted-foreground">
                      {formatTouches(run.touchNumbers)}
                      {run.senders.length > 0 && ` · ${run.senders.join(", ")}`}
                    </div>
                    <div className="mt-0.5 pl-5">
                      <RunOutcome run={run} />
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
          {data.runsTotal > data.runs.length && (
            <div className="px-3 py-2 text-[11px] text-muted-foreground">
              Showing the {data.runs.length} most recent sends of {data.runsTotal}. Use “All Sends”
              in the messages panel to search older ones.
            </div>
          )}
        </div>
      )}
      <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
        Messages sent within {data.sendRunGapMinutes} minutes of each other count as one send.
      </div>
    </div>
  );
}

function TouchProgress({ touches }: { touches: CampaignTouchProgress[] }) {
  if (touches.length === 0) return null;
  return (
    <div className="rounded-md border bg-background">
      <div className="border-b px-3 py-2 text-sm font-medium">Touch Progress</div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-8 text-xs">Touch</TableHead>
            <TableHead className="h-8 text-right text-xs">Sent</TableHead>
            <TableHead className="h-8 text-right text-xs">Queued</TableHead>
            <TableHead className="h-8 text-right text-xs">Replied</TableHead>
            <TableHead className="h-8 text-right text-xs">Bounced</TableHead>
            <TableHead className="h-8 whitespace-nowrap text-xs">Last Sent</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {touches.map((t) => (
            <TableRow key={t.touchNumber}>
              <TableCell className="whitespace-nowrap py-1.5 text-xs font-medium">T{t.touchNumber}</TableCell>
              <TableCell className="whitespace-nowrap py-1.5 text-right text-xs tabular-nums text-emerald-600 dark:text-emerald-400">
                {t.delivered}
                {t.awaitingConfirmation > 0 && (
                  <span className="text-blue-600 dark:text-blue-400"> +{t.awaitingConfirmation}</span>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap py-1.5 text-right text-xs tabular-nums">
                {t.scheduled}
                {t.notReady > 0 && (
                  <span className="text-muted-foreground"> +{t.notReady}d</span>
                )}
              </TableCell>
              <TableCell className="py-1.5 text-right text-xs tabular-nums text-purple-600 dark:text-purple-400">
                {t.replied}
              </TableCell>
              <TableCell className="py-1.5 text-right text-xs tabular-nums text-rose-600 dark:text-rose-400">
                {t.bounced}
              </TableCell>
              <TableCell
                className="whitespace-nowrap py-1.5 text-xs text-muted-foreground"
                title={t.lastSentAt ? formatWhen(t.lastSentAt) : undefined}
              >
                {t.lastSentAt ? formatShortDay(t.lastSentAt) : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
        Sent “+n” = handed to MailBridge, awaiting confirmation · Queued “+nd” = still in draft / QA.
      </div>
    </div>
  );
}

function MessageRow({
  msg,
  expanded,
  onToggle,
}: {
  msg: CampaignMessage;
  expanded: boolean;
  onToggle: () => void;
}) {
  const when = msg.sentAt ?? msg.dispatchedAt;
  const channel = msg.channel && msg.channel !== "email" ? toProperCase(msg.channel) : null;
  return (
    <Fragment>
      <TableRow className="cursor-pointer" onClick={onToggle} aria-expanded={expanded}>
        <TableCell className="py-2 align-top">
          <div className="text-sm font-medium">{msg.prospectName ?? "Unknown prospect"}</div>
          <div className="text-xs text-muted-foreground">{msg.prospectEmail ?? "—"}</div>
          {(msg.prospectTitle || msg.prospectCompany) && (
            <div className="text-[11px] text-muted-foreground">
              {[msg.prospectTitle, msg.prospectCompany].filter(Boolean).join(" · ")}
            </div>
          )}
        </TableCell>
        <TableCell className="py-2 align-top text-xs">
          <div className="font-medium">Touch {msg.touchNumber}</div>
          {channel && <div className="text-muted-foreground">{channel}</div>}
        </TableCell>
        <TableCell className="max-w-[280px] py-2 align-top text-sm">
          <div className="truncate" title={msg.subjectLine ?? undefined}>
            {msg.subjectLine || <span className="italic text-muted-foreground">(no subject)</span>}
          </div>
        </TableCell>
        <TableCell className="py-2 align-top text-xs text-muted-foreground">
          <div>{formatWhen(when)}</div>
          {msg.sentFrom && <div className="truncate">from {msg.sentFrom}</div>}
        </TableCell>
        <TableCell className="py-2 align-top">
          <div className="flex flex-wrap items-center gap-1">
            <MessageStatusBadge status={msg.status} />
            {msg.openedAt && (
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                Opened
              </span>
            )}
            {msg.clickedAt && (
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                Clicked
              </span>
            )}
          </div>
        </TableCell>
        <TableCell className="w-8 py-2 align-top text-muted-foreground">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={6} className="bg-muted/30 py-3">
            <div className="space-y-2">
              <div className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                <div>
                  <span className="text-muted-foreground">Subject: </span>
                  <span className="font-medium">{msg.subjectLine || "(no subject)"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Angle: </span>
                  {msg.angle ? ANGLE_LABEL[msg.angle] ?? toProperCase(msg.angle) : "—"}
                </div>
                <div>
                  <span className="text-muted-foreground">To: </span>
                  {msg.prospectEmail ?? "—"}
                </div>
                <div>
                  <span className="text-muted-foreground">From: </span>
                  {msg.sentFrom ?? "—"}
                </div>
                <div>
                  <span className="text-muted-foreground">Handed to MailBridge: </span>
                  {formatWhen(msg.dispatchedAt)}
                </div>
                <div>
                  <span className="text-muted-foreground">Delivered: </span>
                  {formatWhen(msg.sentAt)}
                </div>
                {msg.openedAt && (
                  <div>
                    <span className="text-muted-foreground">First opened: </span>
                    {formatWhen(msg.openedAt)}
                  </div>
                )}
                {msg.clickedAt && (
                  <div>
                    <span className="text-muted-foreground">First clicked: </span>
                    {formatWhen(msg.clickedAt)}
                  </div>
                )}
                {msg.repliedAt && (
                  <div>
                    <span className="text-muted-foreground">Replied: </span>
                    {formatWhen(msg.repliedAt)}
                  </div>
                )}
                {msg.bouncedAt && (
                  <div>
                    <span className="text-muted-foreground">Bounced: </span>
                    {formatWhen(msg.bouncedAt)}
                  </div>
                )}
                {msg.issueReason && (
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground">Reason: </span>
                    <span className="text-rose-600 dark:text-rose-400">{msg.issueReason}</span>
                  </div>
                )}
              </div>
              <EmailBody body={msg.bodyCopy} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

function MessagesPanel({
  campaignId,
  run,
  scope,
  onScopeChange,
}: {
  campaignId: string;
  run: CampaignSendRun | null;
  scope: "run" | "all";
  onScopeChange: (scope: "run" | "all") => void;
}) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | CampaignMessageStatusFilter>("");
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Debounce typing into the search box.
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const windowStart = scope === "run" && run ? run.startedAt : undefined;
  const windowEnd = scope === "run" && run ? run.endedAt : undefined;

  // Any change to what is being viewed resets paging and the open message.
  useEffect(() => {
    setPage(0);
    setExpandedId(null);
  }, [campaignId, windowStart, windowEnd, search, statusFilter]);

  const { data, isLoading, isFetching, isError } = useQuery<CampaignMessageList>({
    queryKey: [
      "scheduler",
      "campaign-messages",
      campaignId,
      windowStart ?? null,
      windowEnd ?? null,
      search,
      statusFilter,
      page,
    ],
    queryFn: () =>
      schedulerApi.campaignMessages(campaignId, {
        window_start: windowStart,
        window_end: windowEnd,
        q: search || undefined,
        message_status: statusFilter || undefined,
        limit: MESSAGES_PAGE_SIZE,
        offset: page * MESSAGES_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / MESSAGES_PAGE_SIZE));
  const from = total === 0 ? 0 : page * MESSAGES_PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * MESSAGES_PAGE_SIZE);

  const title =
    scope === "run" && run
      ? `Messages Sent ${formatDay(run.startedAt)}, ${formatRunWindow(run)}`
      : "All Messages Sent by This Campaign";

  return (
    <div className="rounded-md border bg-background">
      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <Mail className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{title}</span>
        <div className="ml-auto inline-flex rounded-md border p-0.5 text-xs">
          <button
            type="button"
            disabled={!run}
            onClick={() => onScopeChange("run")}
            className={cn(
              "rounded px-2 py-1 disabled:opacity-50",
              scope === "run" ? "bg-primary text-primary-foreground" : "text-muted-foreground",
            )}
          >
            Selected Send
          </button>
          <button
            type="button"
            onClick={() => onScopeChange("all")}
            className={cn(
              "rounded px-2 py-1",
              scope === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground",
            )}
          >
            All Sends
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search recipient, email, company or subject"
            className="h-8 pl-7 text-sm"
          />
        </div>
        <select
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "" | CampaignMessageStatusFilter)}
        >
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value || "all"} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="space-y-2 p-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : isError ? (
        <div className="p-4 text-sm text-rose-600">Could not load messages. Try Refresh.</div>
      ) : !data || data.items.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          {search || statusFilter ? "No messages match these filters." : "No messages in this view."}
        </div>
      ) : (
        <div className={cn("overflow-x-auto", isFetching && "opacity-70")}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="h-8 text-xs">Recipient</TableHead>
                <TableHead className="h-8 text-xs">Touch</TableHead>
                <TableHead className="h-8 text-xs">Subject</TableHead>
                <TableHead className="h-8 text-xs">Sent</TableHead>
                <TableHead className="h-8 text-xs">Outcome</TableHead>
                <TableHead className="h-8 w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((msg) => (
                <MessageRow
                  key={msg.sequenceId}
                  msg={msg}
                  expanded={expandedId === msg.sequenceId}
                  onToggle={() =>
                    setExpandedId((cur) => (cur === msg.sequenceId ? null : msg.sequenceId))
                  }
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex items-center gap-2 border-t px-3 py-1.5 text-xs text-muted-foreground">
        <span>
          {total === 0 ? "0 messages" : `${from}–${to} of ${plural(total, "message")}`}
          {" · click a row to read the email"}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="tabular-nums">
            {page + 1} / {pageCount}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function CampaignActivityDrilldown({
  campaignId,
  skipReasonLabels,
}: {
  campaignId: string;
  skipReasonLabels: Record<string, string>;
}) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [scope, setScope] = useState<"run" | "all">("run");

  const { data, isLoading, isError, isFetching, refetch } = useQuery<CampaignActivity>({
    queryKey: ["scheduler", "campaign-activity", campaignId],
    queryFn: () => schedulerApi.campaignActivity(campaignId),
    retry: false,
  });

  // Default to the latest send; fall back to it if a selected run vanished.
  const selectedRun = useMemo<CampaignSendRun | null>(() => {
    if (!data) return null;
    return data.runs.find((r) => r.runKey === selectedKey) ?? data.lastRun;
  }, [data, selectedKey]);

  const effectiveScope: "run" | "all" = selectedRun ? scope : "all";

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-16 w-full" />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center gap-3 p-4 text-sm">
        <span className="text-rose-600">Could not load this campaign's send activity.</span>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  const bouncedOrBlocked = data.bounced + data.blocked;

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <Headline data={data} />
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label="Refresh campaign activity"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
        <Stat
          label="Reached"
          value={data.recipientsReached}
          hint={`of ${plural(data.prospectCount, "prospect")}`}
        />
        <Stat
          label="Messages Sent"
          value={data.messagesSent}
          tone="text-emerald-600 dark:text-emerald-400"
          hint={data.firstSentAt ? `since ${formatDay(data.firstSentAt)}` : undefined}
        />
        <Stat label="Replied" value={data.replied} tone="text-purple-600 dark:text-purple-400" />
        <Stat label="Opened" value={data.opened} tone="text-sky-600 dark:text-sky-400" />
        <Stat
          label="Bounced / Blocked"
          value={bouncedOrBlocked}
          tone="text-rose-600 dark:text-rose-400"
          hint={data.blocked > 0 ? `${data.blocked} blocked` : undefined}
        />
        <Stat label="Failed" value={data.failed} />
        <Stat
          label="Awaiting"
          value={data.awaitingConfirmation}
          tone="text-blue-600 dark:text-blue-400"
          hint="delivery confirmation"
        />
        <Stat
          label="Queued"
          value={data.scheduled}
          hint={data.notReady > 0 ? `+${data.notReady} in draft / QA` : "waiting to send"}
        />
      </div> */}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
        <Stat
          label="Messages Sent"
          value={data.messagesSent}
          tone="text-emerald-600 dark:text-emerald-400"
          hint={data.firstSentAt ? `since ${formatDay(data.firstSentAt)}` : undefined}
        />
        <Stat
          label="Reached"
          value={data.recipientsReached}
          hint={`of ${plural(data.prospectCount, "prospect")}`}
        />
        <Stat
          label="Bounced / Blocked"
          value={bouncedOrBlocked}
          tone="text-rose-600 dark:text-rose-400"
          hint={data.blocked > 0 ? `${data.blocked} blocked` : undefined}
        />
        <Stat label="Failed" value={data.failed} />
        <Stat
          label="Awaiting"
          value={data.awaitingConfirmation}
          tone="text-blue-600 dark:text-blue-400"
          hint="delivery confirmation"
        />
        <Stat
          label="Queued"
          value={data.scheduled}
          hint={data.notReady > 0 ? `+${data.notReady} in draft / QA` : "waiting to send"}
        />
        <Stat label="Replied" value={data.replied} tone="text-purple-600 dark:text-purple-400" />
        {/* <Stat label="Opened" value={data.opened} tone="text-sky-600 dark:text-sky-400" /> */}
      </div>

      <SkipNotice data={data} skipReasonLabels={skipReasonLabels} />

      <div className="grid gap-3 lg:grid-cols-3">
        <div className="min-w-0 space-y-3 lg:col-span-1">
          <SendHistory
            data={data}
            selectedKey={selectedRun?.runKey ?? null}
            onSelect={(key) => {
              setSelectedKey(key);
              setScope("run");
            }}
          />
          <TouchProgress touches={data.touches} />
        </div>
        <div className="min-w-0 lg:col-span-2">
          <MessagesPanel
            campaignId={campaignId}
            run={selectedRun}
            scope={effectiveScope}
            onScopeChange={setScope}
          />
        </div>
      </div>
    </div>
  );
}

export default CampaignActivityDrilldown;
